import axios, { AxiosError } from 'axios';

/**
 * Rotas PÚBLICAS do acompanhamento do associado (at-126, ADR 0060) — sem autenticação: o token
 * do link autoriza e o telefone do proprietário confirma quem lê.
 *
 * 🔴 Sem nenhum catch que devolva vazio: "ainda não há etapas" e "não consegui perguntar" levam o
 * associado a conclusões opostas. O erro sobe com o `status`, e quem distingue é a tela.
 */

const publicApi = axios.create({ baseURL: process.env.REACT_APP_BACKEND_URL });

export class ErroDoAcompanhamento extends Error {
  constructor(message: string, readonly status: number | null, readonly code: string | null) {
    super(message);
  }
}

const erro = (e: unknown, fallback: string): ErroDoAcompanhamento => {
  const ax = e as AxiosError<any>;
  const status = ax?.response?.status ?? null;
  const corpo = ax?.response?.data;
  const mensagem = typeof corpo?.message === 'string' ? corpo.message : fallback;
  return new ErroDoAcompanhamento(status ? mensagem : fallback, status, corpo?.code ?? null);
};

export type SituacaoDaEtapa = 'PASSOU' | 'ATUAL' | 'PREVISTA';

export interface AcompanhamentoDoAssociado {
  veiculo: { plate: string; model: string | null; marca: string | null; color: string | null; proprietarioNome: string | null };
  atendimento: {
    entradaEm: string | null;
    entregueEm: string | null;
    diasNaOficina: number | null;
    retornoDeGarantia: { origemEntregueEm: string | null } | null;
  };
  etapaAtual: {
    nome: string;
    desde: string | null;
    previsaoDiasTotal: number | null;
    motivoDaPendencia: string | null;
    emParalelo: string[];
  };
  etapas: { nome: string; situacao: SituacaoDaEtapa; inicio: string | null; fim: string | null }[];
  subtarefas: { etapa: string; nome: string; concluida: boolean }[];
  oficinaExterna: {
    oficina: string;
    entradaEm: string;
    ultimaVisitaEm: string | null;
    etapaInformada: string | null;
    previsaoDaEtapa: string | null;
  } | null;
  checklist: { entrada: any | null; saida: any | null };
  pesquisa: { disponivel: boolean; token: string | null };
}

export async function obterInfoDoAcompanhamento(
  token: string,
): Promise<{ valid: boolean; encerrado: boolean; desativado?: boolean }> {
  try {
    const { data } = await publicApi.get(`/public/acesso-associado/${token}`);
    return data;
  } catch (e) {
    throw erro(e, 'Não foi possível validar o link agora. Tente de novo em instantes.');
  }
}

export async function verificarAcompanhamento(token: string, phone: string): Promise<AcompanhamentoDoAssociado> {
  try {
    const { data } = await publicApi.post(`/public/acesso-associado/${token}/verify`, { phone });
    return data;
  } catch (e) {
    throw erro(e, 'Não conseguimos carregar o andamento agora. Tente de novo em instantes.');
  }
}

export async function baixarPdfDoAcompanhamento(token: string, phone: string): Promise<Blob> {
  try {
    const { data } = await publicApi.post(
      `/public/acesso-associado/${token}/checklist-pdf`,
      { phone },
      { responseType: 'blob' },
    );
    return data as Blob;
  } catch (e) {
    throw erro(e, 'Não foi possível gerar o PDF do checklist agora.');
  }
}

// ------- Rotas INTERNAS (com sessão): a ação "Compartilhar Acompanhamento" e a mensagem -------

const authHeader = (): Record<string, string> => {
  const token = localStorage.getItem('authToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export interface AcompanhamentoShareResult {
  token: string;
  ultimoAcessoEm: string | null;
  plate: string;
  ownerName: string | null;
  ownerPhone: string | null;
  /** Mensagem configurada; `{{link}}` vira o link do acompanhamento. */
  template: string;
}

/** Gera (ou reaproveita) o link do acompanhamento do atendimento. 409 `CAR_WITHOUT_OWNER_PHONE` sem telefone. */
export async function criarShareDoAcompanhamento(carId: string): Promise<AcompanhamentoShareResult> {
  const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/acesso-associado/cars/${carId}/share`, undefined, {
    headers: authHeader(),
    withCredentials: true,
  });
  return response.data;
}

/** O interruptor da feature: desligado, ninguém gera link e os já enviados ficam indisponíveis. */
export interface AtivacaoDoAcompanhamento {
  ativo: boolean;
  alteradoPor: string | null;
  alteradoEm: string | null;
}

export async function obterAtivacaoDoAcompanhamento(): Promise<AtivacaoDoAcompanhamento> {
  const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/acesso-associado/ativacao`, {
    headers: authHeader(),
    withCredentials: true,
  });
  return response.data;
}

export async function definirAtivacaoDoAcompanhamento(ativo: boolean): Promise<AtivacaoDoAcompanhamento> {
  const response = await axios.put(
    `${process.env.REACT_APP_BACKEND_URL}/acesso-associado/ativacao`,
    { ativo },
    { headers: authHeader(), withCredentials: true },
  );
  return response.data;
}

export async function obterMensagemDoAcompanhamento(): Promise<string> {
  const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/acesso-associado/mensagem`, {
    headers: authHeader(),
    withCredentials: true,
  });
  return response.data.value;
}

export async function atualizarMensagemDoAcompanhamento(value: string): Promise<string> {
  const response = await axios.put(
    `${process.env.REACT_APP_BACKEND_URL}/acesso-associado/mensagem`,
    { value },
    { headers: authHeader(), withCredentials: true },
  );
  return response.data.value;
}
